import React, { useEffect, useRef, useState } from 'react';
import { Box, Container, Typography } from '@mui/material';
import { createTheme, ThemeProvider, Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';

import {
	Air,
	AirSimple,
	DestinationSimple,
	DestinationSummaryForDestinationMultiple,
	QuotationDestinationMultipleContainer,
	QuotationServiceSimple,
} from '../../types';
import { GetPAXTotalsResponse } from '../../helpers';
import { calculateTotalNights } from '../../helpers';
import { useAgencyBranding } from '../../branding';
import { QuotationIntroPreview } from './QuotationIntroPreview';
import { QuotationFinalPricePreview } from './QuotationFinalPricePreview';
import { SimpleSelectionSummary, SIMPLE_SUMMARY_BAR_HEIGHT } from './SimpleSelectionSummary';
import PaymentMethods from '../PaymentMethods/PaymentMethods';
import Footer from '../Footer/Footer';
import ImportantInformation from '../ImportantInformation';
import { AirDisplay } from '../AirConfig/AirDisplay';
import { DestinationOptionalServiceList } from '../DestinationOptionalServiceList';
import { CardsCarousel } from '../CardsCarousel';
import { MULTI_SUMMARY_BAR_HEIGHT, MultiSelectHotelsMultiDestination } from '../MultiSelectHotelsMultiDestination';
import { QuotationServicesObservationPreview } from './QuotationServicesObservationPreview';
import { QuotationsConditions } from '../Accordion/QuotationConditions';
import SeguroAsistencia from '../TravelAssistance';
import { CountryCodeEnum } from 'features/common/types';
import { PromoBanner } from 'features/common/components/PromoBanner';
import { WhatsAppBanner } from 'features/common/components/WhatsAppBanner';
import itauLogo from '../../../../assets/itau-logo-white.webp';

// @ts-ignore
const useStyles = makeStyles((theme) => ({
	root: {
		lexGrow: 1,
	},
	paper: {
		marginTop: theme.spacing(4),
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'left',
	},
	itineraryMobileTitle: {
		fontStyle: 'normal',
		fontWeight: 'bold',
		letterSpacing: '0em',
		textAlign: 'center',
		color: theme.palette.primary.main,
		marginBottom: theme.spacing(3),
	},
	presupuestoTitle: {
		fontStyle: 'normal',
		fontWeight: 'bold',
		letterSpacing: '0em',
		textAlign: 'center',
		color: theme.palette.primary.main,
	},
}));

interface Props {
	destinationsForQuotationSimpleDestination: DestinationSimple[] | null;
	containerMultiDestination: QuotationDestinationMultipleContainer | null;
	isMultiDestination: boolean;
	paxTotals: GetPAXTotalsResponse;
	manualServices: QuotationServiceSimple[];
	notIncludes: string[];
	description: string;
	countryCode: CountryCodeEnum;
	airs: Air[] | AirSimple[];
	flightIndexSelected: number | null;
	showFlightSelectionControl?: boolean;
	handleFlightSelectionChange?: (index: number | null) => void;
	exchangeRate: number;
	localCurency: string;
	showLocalCurrency?: boolean;
	quotationId: number;
	ntsQuantity?: number;
	isExternal?: boolean;
}

// Suma de los totales de habitación de una opción: el precio del paquete
// completo para el grupo con ese hotel.
const sumOptionTotal = (option: { rooms: { totalPrice: number }[] }): number =>
	option.rooms.reduce((sum, room) => sum + room.totalPrice, 0);

export default function QuotationPreview(props: Props): JSX.Element {
	const classes = useStyles();
	const branding = useAgencyBranding(props.isExternal);
	const [selectedOptionalsTotalUSD, setSelectedOptionalsTotalUSD] = useState(0);
	const [selectedAssistance, setSelectedAssistance] = useState<{ name: string; totalUSD: number } | null>(null);
	// Simple destino: opción de hotel elegida por destino (índice absoluto en
	// destination.hotelOptions). Multi maneja su propia selección adentro.
	const [selectedHotelByDestination, setSelectedHotelByDestination] = useState<Record<number, number>>({});
	const sectionTitleSx = {
		marginBottom: '10px',
		width: '100%',
		background: branding.headerGradient,
		paddingLeft: '18px',
		paddingRight: '18px',
		paddingTop: '10px',
		paddingBottom: '10px',
		borderRadius: '10px',
		fontSize: '1rem',
		fontWeight: 700,
		color: 'white',
		boxShadow: '0 8px 20px rgba(49, 25, 75, 0.16)',
	};

	const destinationsToUse =
		props.isMultiDestination && props.containerMultiDestination != null
			? props.containerMultiDestination.destinations
			: props.destinationsForQuotationSimpleDestination || [];

	const totalNights = calculateTotalNights(destinationsToUse);

	const flightsRef = useRef([]);

	const handleFlightRef = (element: HTMLDivElement | null, index: number) => {
		// @ts-ignore
		flightsRef.current[index] = element;
	};
	useEffect(() => {
		if (
			props.airs.length > 1 &&
			props.flightIndexSelected != null &&
			flightsRef.current[props.flightIndexSelected] != null
		) {
			// @ts-ignore
			flightsRef.current[props.flightIndexSelected].scrollIntoView({ behavior: 'smooth' });
		}
	}, [props.flightIndexSelected]);

	useEffect(() => {
		// para cuando sea de un solo destino y multiples vuelos se seleccione el vuelo de la opción más económica
		if (
			props.airs.length > 1 &&
			props.destinationsForQuotationSimpleDestination != null &&
			props.destinationsForQuotationSimpleDestination.length == 1 &&
			props.handleFlightSelectionChange != null
		) {
			const newHotelOptionsToSort = [...props.destinationsForQuotationSimpleDestination[0].hotelOptions];
			const priceCombinationsSorted = newHotelOptionsToSort.sort(function (a, b) {
				return (a.rooms[0]?.totalPrice || 0) - (b.rooms[0]?.totalPrice || 0);
			});
			props.handleFlightSelectionChange(priceCombinationsSorted[0].flightIndex ?? null);
		}
	}, []);

	// Simple destino: default y re-validación de la opción elegida.
	// Cuando cambia el vuelo, las opciones visibles cambian (cada opción está
	// atada a un flightIndex): si la elegida ya no es visible, se pasa a la más
	// económica visible — mismo criterio default que usa multi.
	useEffect(() => {
		if (props.isMultiDestination || !props.destinationsForQuotationSimpleDestination) return;
		const destinations = props.destinationsForQuotationSimpleDestination;
		setSelectedHotelByDestination((previous) => {
			const next: Record<number, number> = { ...previous };
			destinations.forEach((destination, destinationIndex) => {
				const visible = destination.hotelOptions
					.map((option, optionIndex) => ({ option, optionIndex }))
					.filter(({ option }) =>
						props.airs.length > 1 && props.flightIndexSelected != null
							? option.flightIndex === props.flightIndexSelected
							: true,
					);
				if (visible.length === 0) {
					delete next[destinationIndex];
					return;
				}
				const current = next[destinationIndex];
				const currentIsVisible = current != null && visible.some(({ optionIndex }) => optionIndex === current);
				if (!currentIsVisible) {
					const cheapest = [...visible].sort((a, b) => sumOptionTotal(a.option) - sumOptionTotal(b.option))[0];
					next[destinationIndex] = cheapest.optionIndex;
				}
			});
			return next;
		});
	}, [props.flightIndexSelected, props.destinationsForQuotationSimpleDestination, props.isMultiDestination]);

	const handleSelectHotelOption = (destinationIndex: number, optionIndex: number) => {
		setSelectedHotelByDestination((previous) => ({ ...previous, [destinationIndex]: optionIndex }));
	};

	const manualServicesWithDescription = props.manualServices.filter(
		(service) => service.description != null && service.description != '',
	);

	// Si la cotización ya trae un seguro/asistencia como servicio incluido,
	// ofrecer comprar otro más abajo contradice el "Incluye:" y genera dudas
	// ("¿entonces no estaba incluido?") — se oculta el bloque de Celeste.
	const includedServiceNames: string[] = destinationsToUse
		// @ts-ignore
		.map((destination: DestinationSimple | DestinationSummaryForDestinationMultiple) =>
			(destination.services || []).map((service) => service.name || ''),
		)
		// @ts-ignore
		.reduce((a, b) => a.concat(b), [])
		.concat(props.manualServices.map((service) => service.name || ''));
	const assistanceAlreadyIncluded = includedServiceNames.some((name) => /asistencia|seguro/i.test(name));

	const servicesWithDescriptionInDestinations = destinationsToUse
		// @ts-ignore
		.map((destination: DestinationSimple | DestinationSummaryForDestinationMultiple) =>
			destination.services.filter((service) => service.description != null && service.description != ''),
		)
		// @ts-ignore
		.reduce((a, b) => a.concat(b), []);

	const showSimpleSummary = !props.isMultiDestination && props.destinationsForQuotationSimpleDestination != null;

	// Colchón inferior para que la barra fija de "Tu selección" no tape el
	// footer. Antes este espacio vivía como padding gigante DENTRO de cada
	// footer (pb 22/14) y se veía como un hueco vacío; ahora lo pone la página.
	// El alto de cada barra lo exporta su componente: antes acá se repetía la
	// heurística (destinos + 1) * 40, que no coincidía con el alto real.
	const bottomBarPadding = showSimpleSummary
		? { xs: `${SIMPLE_SUMMARY_BAR_HEIGHT + 48}px`, sm: `${SIMPLE_SUMMARY_BAR_HEIGHT + 24}px` }
		: props.isMultiDestination
		? { xs: `${MULTI_SUMMARY_BAR_HEIGHT + 48}px`, sm: `${96 + 24}px` }
		: 4;

	// Tipografía por agencia: un ThemeProvider anidado pisa typography.fontFamily
	// para que todas las Typography del preview la hereden. Si la agencia no
	// define fuente, el theme pasa igual (identidad).
	const applyAgencyFont = (outerTheme: Theme): Theme =>
		branding.fontFamily ? createTheme(outerTheme, { typography: { fontFamily: branding.fontFamily } }) : outerTheme;

	return (
		<ThemeProvider theme={applyAgencyFont}>
			<Box
				sx={{
					minHeight: '100vh',
					background: branding.pageWash,
					pb: bottomBarPadding,
					// Desktop: toda la página usa 1200px de ancho útil (los Container
					// md quedan en 900 sólo hasta el breakpoint md).
					'& .MuiContainer-maxWidthMd': {
						px: { xs: 1.5, sm: 3 },
						maxWidth: { md: '1200px' },
					},
				}}
			>
				<PromoBanner countryCode={props.countryCode} isExternal={props.isExternal} />
				<QuotationIntroPreview
					hasHotels={true}
					description={props?.description}
					destinations={destinationsToUse}
					manualServices={props.manualServices}
					notIncludes={props.notIncludes}
					hasAir={props.airs.length > 0}
					isExternal={props.isExternal}
				/>

				{props.airs.length == 1 && (
					<Container component="main" maxWidth="md">
						<div className={classes.paper}>
							<Typography component="h1" variant="h6" sx={sectionTitleSx}>
								Este es el itinerario de vuelos sugerido:
							</Typography>
							<AirDisplay data={props.airs[0]} previewMode={true} index={0} />
						</div>
					</Container>
				)}
				{props.airs.length > 1 && (
					<Container component="main" maxWidth="md">
						<div className={classes.paper}>
							<Typography component="h1" variant="h6" sx={sectionTitleSx}>
								Selecciona la opción de vuelos que desees:
							</Typography>
							<Typography
								variant="body2"
								sx={{
									paddingLeft: '16px',
									paddingRight: '16px',
									paddingBottom: '8px',
									color: 'text.secondary',
									fontWeight: 500,
								}}
							>
								* La tarifa de tu viaje varía automaticamente en función del itinerario elegido.
							</Typography>
							<Container disableGutters sx={{ paddingBottom: 1 }}>
								{/* Mismo criterio que habitaciones y hoteles (CardsCarousel):
								    mobile carrusel con flechas y contador, desktop grilla
								    envolvente para comparar todas las opciones juntas. */}
								<CardsCarousel itemLabel="Opción de vuelo" wrapOnDesktop itemWidth={{ xs: '92%', sm: '75%', md: 520 }}>
									{/*// @ts-ignore*/}
									{props.airs.map((air: Air | AirSimple, index: number) => (
										<div
											key={index}
											ref={(element) => handleFlightRef(element, index)}
											style={{ display: 'flex', width: '100%' }}
										>
											<AirDisplay
												data={air}
												previewMode={true}
												index={index}
												handleSelectionChange={props.handleFlightSelectionChange}
												showSelectionControl={props.showFlightSelectionControl}
												indexSelected={props.flightIndexSelected}
											/>
										</div>
									))}
								</CardsCarousel>
							</Container>
						</div>
					</Container>
				)}
				<Container component="main" maxWidth="md">
					<div className={classes.paper}>
						{!props.isMultiDestination && props.destinationsForQuotationSimpleDestination && (
							<>
								<Typography component="h1" variant="h6" sx={sectionTitleSx}>
									Presupuesto:
								</Typography>
								<QuotationFinalPricePreview
									manualServices={props.manualServices}
									exchangeRate={props.exchangeRate}
									localCurrency={props.localCurency}
									showLocalCurrency={props.showLocalCurrency}
									totalFlights={props.airs.length}
									flightIndexSelected={props.flightIndexSelected}
									destinations={props.destinationsForQuotationSimpleDestination}
									paxTotals={props.paxTotals}
									showUtility={false}
									markup={null}
									quotationId={props.quotationId}
									isExternal={props.isExternal}
									selectedOptionalsTotalUSD={selectedOptionalsTotalUSD}
									selectedAssistanceTotalUSD={selectedAssistance?.totalUSD || 0}
									selectedAssistanceName={selectedAssistance?.name}
									selectedHotelByDestination={selectedHotelByDestination}
									onSelectHotelOption={handleSelectHotelOption}
								/>{' '}
							</>
						)}
						{props.isMultiDestination && props.containerMultiDestination && (
							<>
								<Typography component="h1" variant="h6" sx={sectionTitleSx}>
									Selecciona el alojamiento para cada destino:
								</Typography>
								<Typography
									variant="body2"
									sx={{
										paddingLeft: '16px',
										paddingRight: '16px',
										paddingBottom: '8px',
										color: 'text.secondary',
										fontWeight: 500,
									}}
								>
									* La tarifa de tu viaje varía automaticamente en función del alojamiento elegido para cada destino.
								</Typography>
								<MultiSelectHotelsMultiDestination
									exchangeRate={props.exchangeRate}
									localCurrency={props.localCurency}
									showLocalCurrency={props.showLocalCurrency}
									destinations={props.containerMultiDestination.destinations}
									priceCombinations={props.containerMultiDestination.priceCombinations}
									paxTotals={props.paxTotals}
									manualServices={props.manualServices}
									showUtility={false}
									markup={null}
									totalFlights={props.airs.length}
									flightIndexSelected={props.flightIndexSelected}
									handleFlightSelectionChange={props.handleFlightSelectionChange}
									quotationId={props.quotationId}
									isExternal={props.isExternal}
									selectedOptionalsTotalUSD={selectedOptionalsTotalUSD}
									selectedAssistanceTotalUSD={selectedAssistance?.totalUSD || 0}
									selectedAssistanceName={selectedAssistance?.name}
								/>{' '}
							</>
						)}
					</div>
				</Container>
				{(manualServicesWithDescription.length > 0 || servicesWithDescriptionInDestinations.length > 0) && (
					<QuotationServicesObservationPreview
						services={servicesWithDescriptionInDestinations}
						manualServices={manualServicesWithDescription}
						isExternal={props.isExternal}
					/>
				)}
				<DestinationOptionalServiceList
					destinations={destinationsToUse}
					paxTotals={props.paxTotals}
					isPreview={true}
					isExternal={props.isExternal}
					onPreviewOptionalTotalChange={setSelectedOptionalsTotalUSD}
				/>
				{props.isExternal && (
					<Container component="main" maxWidth="md" sx={{ mb: 2 }}>
						<Box
							sx={{
								backgroundColor: '#f46206',
								borderRadius: '8px',
								display: 'flex',
								alignItems: 'center',
								gap: 2,
								p: 2,
							}}
						>
							<img src={itauLogo} alt="Itaú" style={{ height: 52, objectFit: 'contain' }} />
							<Typography variant="body1" sx={{ color: 'white', flex: 1 }}>
								<strong>18 cuotas</strong> sin recargo y <strong>millas preferenciales</strong> con tus tarjetas Itaú
								Volar
							</Typography>
						</Box>
					</Container>
				)}
				{!assistanceAlreadyIncluded && (
					<SeguroAsistencia
						ntsQuantity={totalNights}
						country={props.countryCode}
						exchangeRate={props.exchangeRate}
						isExternal={props.isExternal}
						onAssistanceSelectionChange={setSelectedAssistance}
						totalPassengers={props.paxTotals.adults + props.paxTotals.child + props.paxTotals.infants}
					/>
				)}

				<WhatsAppBanner countryCode={props.countryCode} />

				<PaymentMethods country={props.countryCode} isExternal={props.isExternal} />
				<QuotationsConditions isExternal={props.isExternal} />
				<ImportantInformation country={props.countryCode} isExternal={props.isExternal} />
				<Footer country={props.countryCode} isExternal={props.isExternal} />

				{showSimpleSummary && props.destinationsForQuotationSimpleDestination && (
					<SimpleSelectionSummary
						quotationId={props.quotationId}
						isExternal={props.isExternal}
						manualServices={props.manualServices}
						destinations={props.destinationsForQuotationSimpleDestination}
						paxTotals={props.paxTotals}
						airs={props.airs as AirSimple[]}
						flightIndexSelected={props.flightIndexSelected}
						selectedHotelByDestination={selectedHotelByDestination}
						exchangeRate={props.exchangeRate}
						localCurrency={props.localCurency}
						showLocalCurrency={props.showLocalCurrency}
						selectedOptionalsTotalUSD={selectedOptionalsTotalUSD}
						selectedAssistanceTotalUSD={selectedAssistance?.totalUSD || 0}
						selectedAssistanceName={selectedAssistance?.name}
					/>
				)}
			</Box>
		</ThemeProvider>
	);
}
